import { useEffect, useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AddressAutocomplete } from "@/components/site/AddressAutocomplete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { LogOut, Inbox, Loader2, RefreshCw, Mail, Phone, MapPin, Pencil, Trash2, CalendarPlus, UserPlus } from "lucide-react";

const STATUSES = ["new", "contacted", "scheduled", "closed"];
const STATUS_STYLE = {
  new: "bg-rust text-white",
  contacted: "bg-ink text-paper",
  scheduled: "bg-blue-600 text-white",
  closed: "bg-ink/20 text-ink",
};
const TIMES = ["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM"];
const EMPTY_LEAD = { name: "", email: "", phone: "", address: "", service_type: "", message: "", notes: "" };

const fieldCls = "mt-1.5 bg-white border-ink/20 focus-visible:ring-rust";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");

  const [editLead, setEditLead] = useState(null);       // lead being edited (or null)
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_LEAD);
  const [bookLead, setBookLead] = useState(null);        // lead being booked
  const [bookForm, setBookForm] = useState({ date: "", time: "", address: "", city: "", zip: "", notify_customer: false });
  const [deleteLead, setDeleteLead] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => toast.error("Failed to load leads"));
  useEffect(() => { if (user) load(); }, [user]);

  const updateStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.patch(`/leads/${id}/status`, { status }); toast.success("Status updated"); }
    catch { toast.error("Update failed"); load(); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${editLead.id}`, {
        name: editLead.name, email: editLead.email, phone: editLead.phone, address: editLead.address,
        service_type: editLead.service_type, message: editLead.message, notes: editLead.notes,
      });
      setLeads((ls) => ls.map((l) => (l.id === data.id ? data : l)));
      toast.success("Customer updated");
      setEditLead(null);
    } catch { toast.error("Failed to save changes"); }
    finally { setSaving(false); }
  };

  const saveAdd = async () => {
    if (!addForm.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/leads", addForm);
      setLeads((ls) => [data, ...(ls || [])]);
      toast.success("Customer added");
      setAddOpen(false); setAddForm(EMPTY_LEAD);
    } catch { toast.error("Failed to add customer"); }
    finally { setSaving(false); }
  };

  const saveBooking = async () => {
    if (!bookForm.date || !bookForm.time) { toast.error("Pick a date and time"); return; }
    setSaving(true);
    try {
      const nice = new Date(bookForm.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
      const fullAddress = bookForm.address.trim()
        ? `${bookForm.address.trim()}, ${bookForm.city.trim()}, MN${bookForm.zip.trim() ? ` ${bookForm.zip.trim()}` : ""}`
        : "";
      await api.post(`/leads/${bookLead.id}/admin-booking`, {
        date: nice, time: bookForm.time, address: fullAddress, notify_customer: bookForm.notify_customer,
      });
      toast.success(bookForm.notify_customer ? "Appointment booked & customer emailed" : "Appointment booked");
      setBookLead(null);
      load();
    } catch { toast.error("Failed to book appointment"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteLead.id;
    setDeleteLead(null);
    try { await api.delete(`/leads/${id}`); setLeads((ls) => ls.filter((l) => l.id !== id)); toast.success("Customer deleted"); }
    catch { toast.error("Delete failed"); load(); }
  };

  const stats = useMemo(() => {
    const all = leads || [];
    return {
      total: all.length,
      new: all.filter((l) => l.status === "new").length,
      scheduled: all.filter((l) => l.status === "scheduled").length,
      closed: all.filter((l) => l.status === "closed").length,
    };
  }, [leads]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    return filter === "all" ? leads : leads.filter((l) => l.status === filter);
  }, [leads, filter]);

  if (user === null) return <div className="min-h-screen bg-ink grid place-items-center"><Loader2 className="animate-spin text-rust" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-ink text-paper border-b border-paper/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 bg-rust text-white grid place-items-center font-display font-black">G</span>
            <div>
              <p className="font-display font-bold leading-none">Leads Dashboard</p>
              <p className="font-mono text-[11px] text-paper/50 mt-1">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="add-customer-btn" onClick={() => { setAddForm(EMPTY_LEAD); setAddOpen(true); }}
              className="inline-flex items-center gap-2 bg-rust text-white px-4 py-2.5 text-sm font-semibold hover:bg-rust/90 transition-colors">
              <UserPlus size={16} /> Add Customer
            </button>
            <button data-testid="refresh-btn" onClick={load} className="p-2.5 border border-paper/20 hover:border-rust transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 border border-paper/20 px-4 py-2.5 text-sm font-medium hover:border-rust transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[["Total Leads", stats.total], ["New", stats.new], ["Scheduled", stats.scheduled], ["Closed", stats.closed]].map(([label, val]) => (
            <div key={label} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="bg-surface border border-ink/10 p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-ink/50">{label}</p>
              <p className="mt-2 font-display font-black text-4xl text-ink">{val}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {["all", ...STATUSES].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
              className={`px-4 py-2 text-sm font-medium border capitalize transition-colors ${
                filter === s ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/70 hover:border-ink"
              }`}>{s}</button>
          ))}
        </div>

        {leads === null ? (
          <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-rust" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-24 flex flex-col items-center text-ink/50 border border-dashed border-ink/20">
            <Inbox size={40} /><p className="mt-4">No leads {filter !== "all" ? `in "${filter}"` : "yet"}.</p>
          </div>
        ) : (
          <div className="border border-ink/10 bg-surface overflow-x-auto">
            <table data-testid="leads-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink/10 text-left font-mono text-xs uppercase tracking-widest text-ink/50">
                  <th className="p-4">Customer</th>
                  <th className="p-4 hidden md:table-cell">Contact</th>
                  <th className="p-4 hidden lg:table-cell">Service</th>
                  <th className="p-4 hidden xl:table-cell">Notes</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10">
                {filtered.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-paper/60 align-top">
                    <td className="p-4">
                      <p className="font-semibold text-ink">{l.name}</p>
                      <p className="font-mono text-xs text-ink/50 mt-1">{new Date(l.created_at).toLocaleDateString()}</p>
                      <p className="text-xs text-ink/60 flex items-center gap-1 mt-1"><MapPin size={12} /> {l.address || "—"}</p>
                      {l.booking?.date && <p className="text-xs text-blue-600 flex items-center gap-1 mt-1"><CalendarPlus size={12} /> {l.booking.date} · {l.booking.time}</p>}
                    </td>
                    <td className="p-4 hidden md:table-cell text-ink/70">
                      {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-rust transition-colors"><Mail size={13} /> {l.email}</a>}
                      {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 mt-1 hover:text-rust transition-colors"><Phone size={13} /> {l.phone}</a>}
                    </td>
                    <td className="p-4 hidden lg:table-cell text-ink/80">{l.service_type || "—"}</td>
                    <td className="p-4 hidden xl:table-cell text-ink/60 max-w-xs">{l.notes || "—"}</td>
                    <td className="p-4">
                      <select data-testid={`status-select-${l.id}`} value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wide border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-rust ${STATUS_STYLE[l.status] || ""}`}>
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-white text-ink">{s}</option>)}
                      </select>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-btn-${l.id}`} onClick={() => setEditLead({ ...EMPTY_LEAD, ...l })} title="Edit"
                          className="p-2 border border-ink/15 hover:border-rust hover:text-rust transition-colors"><Pencil size={14} /></button>
                        <button data-testid={`book-btn-${l.id}`} onClick={() => { setBookForm({ date: "", time: "", address: l.address || "", city: "", zip: "", notify_customer: false }); setBookLead(l); }} title="Book appointment"
                          className="p-2 border border-ink/15 hover:border-blue-600 hover:text-blue-600 transition-colors"><CalendarPlus size={14} /></button>
                        <button data-testid={`delete-btn-${l.id}`} onClick={() => setDeleteLead(l)} title="Delete"
                          className="p-2 border border-ink/15 hover:border-red-600 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={!!editLead} onOpenChange={(o) => !o && setEditLead(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-dialog">
          <DialogHeader><DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>Update this customer's contact details and private notes.</DialogDescription></DialogHeader>
          {editLead && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input data-testid="edit-name" value={editLead.name} onChange={(e) => setEditLead({ ...editLead, name: e.target.value })} className={fieldCls} /></div>
              <div><Label>Email</Label><Input data-testid="edit-email" value={editLead.email} onChange={(e) => setEditLead({ ...editLead, email: e.target.value })} className={fieldCls} /></div>
              <div><Label>Phone</Label><Input data-testid="edit-phone" value={editLead.phone} onChange={(e) => setEditLead({ ...editLead, phone: e.target.value })} className={fieldCls} /></div>
              <div><Label>Address</Label><AddressAutocomplete testId="edit-address" value={editLead.address} placeholder="123 Main St, City, MN" className={fieldCls}
                onChange={(v) => setEditLead({ ...editLead, address: v })}
                onSelect={({ formatted }) => setEditLead({ ...editLead, address: formatted })} /></div>
              <div className="sm:col-span-2"><Label>Service</Label><Input data-testid="edit-service" value={editLead.service_type} onChange={(e) => setEditLead({ ...editLead, service_type: e.target.value })} className={fieldCls} /></div>
              <div className="sm:col-span-2"><Label>Notes (private)</Label><Textarea data-testid="edit-notes" value={editLead.notes} onChange={(e) => setEditLead({ ...editLead, notes: e.target.value })} className={fieldCls} rows={3} /></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditLead(null)} className="px-4 py-2.5 border border-ink/20 text-sm font-medium hover:border-ink transition-colors">Cancel</button>
            <button data-testid="save-edit-btn" onClick={saveEdit} disabled={saving} className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 text-sm font-semibold hover:bg-rust transition-colors disabled:opacity-60">
              {saving && <Loader2 size={15} className="animate-spin" />} Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD DIALOG */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-dialog">
          <DialogHeader><DialogTitle>Add Customer</DialogTitle>
            <DialogDescription>Manually create a new customer record in your CRM.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input data-testid="add-name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className={fieldCls} /></div>
            <div><Label>Email</Label><Input data-testid="add-email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className={fieldCls} /></div>
            <div><Label>Phone</Label><Input data-testid="add-phone" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className={fieldCls} /></div>
            <div><Label>Address</Label><AddressAutocomplete testId="add-address" value={addForm.address} placeholder="123 Main St, City, MN" className={fieldCls}
              onChange={(v) => setAddForm({ ...addForm, address: v })}
              onSelect={({ formatted }) => setAddForm({ ...addForm, address: formatted })} /></div>
            <div className="sm:col-span-2"><Label>Service</Label><Input data-testid="add-service" value={addForm.service_type} onChange={(e) => setAddForm({ ...addForm, service_type: e.target.value })} className={fieldCls} /></div>
            <div className="sm:col-span-2"><Label>Notes (private)</Label><Textarea data-testid="add-notes" value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} className={fieldCls} rows={3} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setAddOpen(false)} className="px-4 py-2.5 border border-ink/20 text-sm font-medium hover:border-ink transition-colors">Cancel</button>
            <button data-testid="save-add-btn" onClick={saveAdd} disabled={saving} className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 text-sm font-semibold hover:bg-rust transition-colors disabled:opacity-60">
              {saving && <Loader2 size={15} className="animate-spin" />} Add Customer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOOK DIALOG */}
      <Dialog open={!!bookLead} onOpenChange={(o) => !o && setBookLead(null)}>
        <DialogContent className="max-w-md" data-testid="book-dialog">
          <DialogHeader><DialogTitle>Book Appointment{bookLead ? ` — ${bookLead.name}` : ""}</DialogTitle>
            <DialogDescription>Schedule a visit and optionally email the customer a confirmation.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Date</Label><Input data-testid="book-date" type="date" value={bookForm.date} onChange={(e) => setBookForm({ ...bookForm, date: e.target.value })} className={fieldCls} /></div>
            <div>
              <Label>Time</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {TIMES.map((t) => (
                  <button key={t} type="button" data-testid={`book-time-${t.replace(/[\s:]/g, "")}`} onClick={() => setBookForm({ ...bookForm, time: t })}
                    className={`px-2 py-2 text-xs font-medium border transition-colors ${bookForm.time === t ? "bg-blue-600 text-white border-blue-600" : "border-ink/20 text-ink/80 hover:border-ink"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div><Label>Service address</Label><AddressAutocomplete testId="book-address" value={bookForm.address} placeholder="123 Main St" className={fieldCls}
              onChange={(v) => setBookForm({ ...bookForm, address: v })}
              onSelect={({ street, city, zip }) => setBookForm((b) => ({ ...b, address: street, city: city || b.city, zip: zip || b.zip }))} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-3"><Label className="text-[10px] uppercase tracking-widest text-ink/50">City</Label><Input data-testid="book-city" value={bookForm.city} onChange={(e) => setBookForm({ ...bookForm, city: e.target.value })} placeholder="Minneapolis" className={fieldCls} /></div>
              <div className="sm:col-span-1"><Label className="text-[10px] uppercase tracking-widest text-ink/50">State</Label><Input data-testid="book-state" value="MN" readOnly disabled className="mt-1.5 bg-ink/5 border-ink/20 text-ink/60" /></div>
              <div className="sm:col-span-2"><Label className="text-[10px] uppercase tracking-widest text-ink/50">Zip</Label><Input data-testid="book-zip" value={bookForm.zip} onChange={(e) => setBookForm({ ...bookForm, zip: e.target.value })} placeholder="55401" className={fieldCls} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink/80 cursor-pointer">
              <Checkbox data-testid="book-notify" checked={bookForm.notify_customer} onCheckedChange={(v) => setBookForm({ ...bookForm, notify_customer: !!v })} />
              Email the customer a confirmation
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setBookLead(null)} className="px-4 py-2.5 border border-ink/20 text-sm font-medium hover:border-ink transition-colors">Cancel</button>
            <button data-testid="save-booking-btn" onClick={saveBooking} disabled={saving} className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60">
              {saving && <Loader2 size={15} className="animate-spin" />} Book Appointment
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={!!deleteLead} onOpenChange={(o) => !o && setDeleteLead(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{deleteLead?.name}</strong> and all their details. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
